// Shared layout constants for the dense "one row per user" dashboard charts.
//
// ApexCharts sizes a horizontal bar chart by dividing the available grid height
// by the number of data points, so a chart given a fixed height silently shrinks
// each row as the row count grows until the labels overlap. These charts instead
// derive their height from the row count and scroll inside the widget body.
export const CHART_ROW_PITCH = 28;

// Vertical space allowed for a top aligned legend, which sits inside the chart
// height rather than beside it.
//
// This must be ENFORCED via `legend.height`, not just added to the total. Case
// statuses are customer configurable and Apex wraps a top legend, so an unpinned
// legend grows without limit and eats the plot area: measured at the four column
// minimum width, 12 long statuses produced a 288px legend and collapsed the row
// pitch to 13.35px, and 16 statuses put the row labels back into overlap. Pinning
// it keeps the pitch constant at any status count, and the legend scrolls inside
// the band (see the `.apexcharts-legend` rule in CaseTypesPerUserWidget).
export const CHART_LEGEND_HEIGHT = 40;

/**
 * Height for a dense chart with `rowCount` rows, so every row keeps the same
 * readable pitch no matter how many users the tenant has.
 *
 * Charts with a legend add `CHART_LEGEND_HEIGHT` at the call site, so the legend
 * allowance stays visible next to the `legend.height` that enforces it.
 */
export const denseChartHeight = (rowCount: number): number =>
  Math.max(rowCount, 1) * CHART_ROW_PITCH;

/**
 * Row labels are drawn inside the plot area, where Apex will not truncate them.
 * Cap the length so a long name cannot run across the chart, and rely on the
 * hover tooltip to show the full value.
 */
export const truncateLabel = (label: string, max = 30): string => {
  if (!label) return "";
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
};
